import { computed, inject, Injectable, signal } from '@angular/core';
import { map, Observable, of, shareReplay, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { APP_CONFIG } from '../config/app-config';
import { ApiHttpService } from '../http/api-http.service';

/** Effective identity + roles for the current caller (mirrors the backend `MeDto`). */
export interface MeInfo {
  userId: string | null;
  name: string | null;
  isActive: boolean;
  isAdmin: boolean;
  roles: string[];
  facility: string | null;
  manager: string | null;
}

/** Roles that grant unrestricted RIPT record-type access. */
const FULL_ACCESS_ROLES = ['Admin'];

/**
 * Resolves the signed-in user's *effective* role from the API `/me` endpoint (RoleMaster-driven,
 * server-side authority) rather than the raw token claims — so admin-only UI reflects the database
 * assignment. When auth is disabled (local dev) the user is treated as an admin.
 */
@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  private readonly api = inject(ApiHttpService);
  private readonly config = inject(APP_CONFIG);

  private readonly _isAdmin = signal(false);
  private readonly _isActive = signal(true);
  private readonly _roles = signal<string[]>([]);
  private readonly _name = signal<string | null>(null);
  private readonly _facility = signal<string | null>(null);
  private readonly _manager = signal<string | null>(null);
  private readonly _loaded = signal(false);
  readonly isAdmin = this._isAdmin.asReadonly();
  readonly isActive = this._isActive.asReadonly();
  readonly roles = this._roles.asReadonly();
  /** Effective display name from the API `/me` endpoint; null until resolved. */
  readonly name = this._name.asReadonly();
  /** Caller's facility from the API `/me` endpoint; null when unmanaged/unresolved. */
  readonly facility = this._facility.asReadonly();
  /** Caller's manager name from the API `/me` endpoint; null when none/unresolved. */
  readonly manager = this._manager.asReadonly();

  /** Record types the caller is allowed to create (drives the chooser + form-route guard). */
  readonly allowedRecordTypes = computed<string[]>(() =>
    (['Standalone', 'Worklist'] as const).filter((t) => this.canCreateRecordType(t)),
  );

  private inflight?: Observable<boolean>;

  /** True when the caller may create/access the given RIPT record type. */
  canCreateRecordType(recordType: string): boolean {
    if (!this.config.authEnabled) {
      return true;
    }
    const roles = this._roles();
    if (roles.some((r) => FULL_ACCESS_ROLES.includes(r))) {
      return true;
    }
    if (/^standalone$/i.test(recordType)) {
      return roles.includes('RIPT Standalone');
    }
    if (/^worklist$/i.test(recordType)) {
      return roles.includes('RIPT-Worklist');
    }
    return false;
  }

  /** Loads (once) the caller's admin status. Safe to call repeatedly; cached after first resolve. */
  load(): Observable<boolean> {
    if (!this.config.authEnabled) {
      this._isAdmin.set(true);
      this._isActive.set(true);
      this._loaded.set(true);
      return of(true);
    }
    if (this._loaded()) {
      return of(this._isAdmin());
    }
    this.inflight ??= this.api.get<MeInfo>('me').pipe(
      tap((me) => {
        this._isActive.set(me.isActive);
        this._isAdmin.set(me.isAdmin);
        this._roles.set(me.roles ?? []);
        this._name.set(me.name);
        this._facility.set(me.facility);
        this._manager.set(me.manager);
        this._loaded.set(true);
        this.inflight = undefined;
      }),
      map((me) => me.isAdmin),
      catchError(() => of(false)),
      shareReplay(1),
    );
    return this.inflight;
  }
}
